import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { APP_VERSION, PROPAGANDA_LINES } from "../const.ts";

const DWELL_MS = 6000;
const SCROLL_SPEED_PX_PER_SEC = 60;
const MIN_SCROLL_MS = 2500;
const HOLD_START_MS = 1200;
const HOLD_END_MS = 1200;

const HISTORY_SIZE = Math.min(
	Math.max(0, PROPAGANDA_LINES.length - 1),
	Math.max(5, Math.floor(PROPAGANDA_LINES.length / 3)),
);

function pickNextIndex(history: readonly number[]): number {
	const banned = new Set(history);
	const candidates: number[] = [];
	for (let i = 0; i < PROPAGANDA_LINES.length; i++) {
		if (!banned.has(i)) {
			candidates.push(i);
		}
	}
	if (candidates.length === 0) {
		return Math.floor(Math.random() * PROPAGANDA_LINES.length);
	}
	return candidates[Math.floor(Math.random() * candidates.length)];
}

export function PropagandaTicker() {
	const [index, setIndex] = useState(() => pickNextIndex([]));
	const [measureKey, setMeasureKey] = useState(0);
	const historyRef = useRef<number[]>([]);
	if (historyRef.current.length === 0) {
		historyRef.current = [index];
	}
	const containerRef = useRef<HTMLDivElement>(null);
	const lineRef = useRef<HTMLSpanElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: index and measureKey are trigger signals; the effect reads DOM via refs, not these values directly.
	useLayoutEffect(() => {
		const container = containerRef.current;
		const line = lineRef.current;
		if (!container || !line) {
			return;
		}

		line.style.transform = "translateX(0)";

		const overflow = line.scrollWidth - container.clientWidth;
		const advance = () => {
			const next = pickNextIndex(historyRef.current);
			historyRef.current = [...historyRef.current, next].slice(-HISTORY_SIZE);
			setIndex(next);
		};
		const reduceMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		if (overflow <= 0 || reduceMotion) {
			const timeout = window.setTimeout(advance, DWELL_MS);
			return () => {
				window.clearTimeout(timeout);
			};
		}

		const scrollMs = Math.max(
			MIN_SCROLL_MS,
			(overflow / SCROLL_SPEED_PX_PER_SEC) * 1000,
		);
		const totalMs = HOLD_START_MS + scrollMs + HOLD_END_MS;
		const scrollStartOffset = HOLD_START_MS / totalMs;
		const scrollEndOffset = (HOLD_START_MS + scrollMs) / totalMs;

		const animation = line.animate(
			[
				{ transform: "translateX(0)", offset: 0 },
				{ transform: "translateX(0)", offset: scrollStartOffset },
				{
					transform: `translateX(${-overflow}px)`,
					offset: scrollEndOffset,
				},
				{ transform: `translateX(${-overflow}px)`, offset: 1 },
			],
			{ duration: totalMs, easing: "linear", fill: "forwards" },
		);
		animation.onfinish = advance;

		return () => {
			animation.cancel();
		};
	}, [index, measureKey]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		const observer = new ResizeObserver(() => {
			setMeasureKey((k) => k + 1);
		});
		observer.observe(container);
		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<div className="flex items-center gap-4 border-b-2 border-surface-container-highest bg-surface-container-lowest px-4 py-1 font-mono text-[10px] tracking-widest text-outline uppercase md:px-6">
			<span className="shrink-0 whitespace-nowrap text-primary-container">
				{"BROADCAST //"}
			</span>
			<div ref={containerRef} className="flex-1 overflow-hidden">
				<span
					ref={lineRef}
					key={index}
					className="inline-block whitespace-nowrap text-on-surface-variant"
				>
					{PROPAGANDA_LINES[index]}
				</span>
			</div>
			<span className="hidden shrink-0 whitespace-nowrap text-outline-variant md:inline">
				MANUAL v{APP_VERSION}
			</span>
		</div>
	);
}
